import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import useSheetData from "../hooks/useSheetData";
import Reveal from "../components/common/Reveal";
import Icon from "../components/common/Icon";
import { SheetLoading } from "../components/common/SheetState";
import Seo from "../components/common/Seo";
import { colors, fonts, btnOutline, sectionPad, maxW } from "../styles/theme";
import slugify from "../utils/slugify";

export default function BlogPostPage() {
  const mob = useIsMobile();
  const { slug } = useParams();
  const { data: posts, status } = useSheetData("Blog");
  const post = posts.find((b) => (b.slug || slugify(b.title)) === slug);

  const articleJsonLd = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      datePublished: post.date,
      articleSection: post.cat,
      author: { "@type": "Organization", name: "Charith" },
      publisher: { "@type": "Organization", name: "Charith", logo: { "@type": "ImageObject", url: "https://charith.it.com/logo512.png" } },
      mainEntityOfPage: `https://charith.it.com/blog/${slug}`,
    };
  }, [post, slug]);

  if (status === "loading") {
    return (
      <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SheetLoading label="Loading article…" />
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74, display: "flex", alignItems: "center" }}>
        <Seo title="Post Not Found | Charith Blog" description="This article may have moved or hasn't been published yet." path={`/blog/${slug}`} />
        <Reveal style={{ ...maxW, textAlign: "center", padding: "80px 6%" }}>
          <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 30, color: colors.ink, marginBottom: 14 }}>Post not found</h1>
          <p style={{ color: colors.inkFaint, fontFamily: fonts.body, fontSize: 15, marginBottom: 28 }}>
            {status === "error"
              ? "We couldn't reach the blog right now — please try again shortly."
              : "This article may have moved or hasn't been published yet."}
          </p>
          <Link to="/blog" style={{ ...btnOutline, textDecoration: "none", display: "inline-block" }}>Back to Blog</Link>
        </Reveal>
      </div>
    );
  }

  const paragraphs = String(post.content || "").split(/\n+/).filter(Boolean);

  return (
    <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74 }}>
      <Seo
        title={`${post.title} | Charith Blog`}
        description={paragraphs[0] ? paragraphs[0].slice(0, 160) : post.title}
        path={`/blog/${slug}`}
        jsonLd={articleJsonLd}
      />
      <section style={{ background: colors.paper, ...sectionPad(mob), paddingBottom: mob ? 30 : 40 }}>
        <Reveal style={{ ...maxW, maxWidth: 760 }}>
          <Link to="/blog" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: colors.inkFaint, fontFamily: fonts.body, fontSize: 13, fontWeight: 600, textDecoration: "none", marginBottom: 26 }}>
            <Icon name="ArrowRight" size={14} style={{ transform: "rotate(180deg)" }} /> Back to Blog
          </Link>
          <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ border: `1px solid ${colors.borderStrong}`, color: colors.rust, fontFamily: fonts.body, fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 20 }}>{post.cat}</span>
            <span style={{ color: colors.inkFainter, fontFamily: fonts.body, fontSize: 12.5 }}>{post.date} · {post.read} read</span>
          </div>
          <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 28 : "clamp(28px,3.6vw,46px)", lineHeight: 1.2, color: colors.ink, letterSpacing: "-.01em" }}>{post.title}</h1>
        </Reveal>
      </section>

      <section style={{ background: colors.paper, padding: mob ? "0 6% 64px" : "0 6% 100px" }}>
        <Reveal delay={0.1} style={{ maxWidth: 760, margin: "0 auto", borderTop: `1px solid ${colors.border}`, paddingTop: 36 }}>
          {paragraphs.map((p, i) => (
            <p key={i} style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: mob ? 15.5 : 17, lineHeight: 1.85, marginBottom: 22 }}>{p}</p>
          ))}
        </Reveal>
      </section>
    </div>
  );
}
